import { useEffect, useState, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStreaks, getLeaderboard, sendCongratulation } from "../utils/streakUtils";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { examTypes } from "../utils/config";
import "../styles/StreakPage.css";
import Button from "../components/ui/Button";
import Avatar from "../components/Avatar";

function StreakPage() {
  const auth = getAuth();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const userExamType = localStorage.getItem("examType") || "TYT–AYT";
  const [streaks, setStreaks] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [selectedExam, setSelectedExam] = useState(userExamType);
  const [error, setError] = useState("");
  const [motivationalMessage, setMotivationalMessage] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const navigate = useNavigate();

  const motivationalMessages = [
    { streak: 0, text: "Bugün zinciri başlat! 🔥" },
    { streak: 1, text: "Harika bir başlangıç! Devam et! 💪" },
    { streak: 3, text: "3 gündür harikasın, Plan Ustası olmaya az kaldı! 🌟" },
    { streak: 7, text: "Bir hafta oldu, durdurulamazsın! 🚀" },
    { streak: 15, text: "15 gün! Gerçek bir streak savaşçısısın! 🥇" },
    { streak: 30, text: "Efsane! 30 gün boyunca zinciri koparmadın! ✨" },
  ];

  const getMotivationalMessage = (streakCount) => {
    const message = motivationalMessages
      .filter((msg) => streakCount >= msg.streak)
      .sort((a, b) => b.streak - a.streak)[0];
    return message ? message.text : "Bugün zinciri kırma! 🔥";
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;

      try {
        const myStreak = await getStreaks(userId);
        setStreaks(myStreak);
        setMotivationalMessage(getMotivationalMessage(myStreak.currentStreak || 0));

        if (activeTab === "weekly" || activeTab === "monthly") {
          const { top10, userRankData } = await getLeaderboard(
            selectedExam,
            activeTab === "weekly" ? "weekly" : "monthly",
            userId
          );
          setLeaderboard(top10);
          setUserRank(userRankData);
          if (top10.length === 0) {
            setError("Liderlik tablosu şu anda boş. Hemen çalışmaya başla ve zirveye çık! 🏆");
          } else {
            setError("");
          }
        }
      } catch (err) {
        console.error("Veri çekme hatası:", err.message, err.code);
        setError("Liderlik tablosunu yüklerken bir hata oluştu.");
        setLeaderboard([]);
      }
    };

    if (!loading) {
      fetchData();
    }
  }, [userId, selectedExam, activeTab, loading]); // Added activeTab to trigger re-fetch on change

  const handleCongratulate = async (toUserId) => {
    try {
      await sendCongratulation(userId, toUserId);
      toast.success("Tebrik gönderildi! 🎉");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const chartData = useMemo(
    () => [
      { name: "Giriş", count: streaks?.login?.count || 0 },
      { name: "Hedef", count: streaks?.goal?.count || 0 },
      { name: "Plan", count: streaks?.plan?.count || 0 },
      { name: "Çalışma", count: streaks?.study?.count || 0 },
    ],
    [streaks]
  );

  if (loading) {
    return <div className="text-center text-secondary py-10">Yükleniyor...</div>;
  }

  // Podyum (İlk 3)
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  // Podium order: 2nd, 1st, 3rd
  const podiumOrder = [
    top3.find(u => u.rank === 2),
    top3.find(u => u.rank === 1),
    top3.find(u => u.rank === 3)
  ];

  return (
    <motion.div
      className="streak-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="streak-background"></div>

      <div className="streak-container">
        
        <div className="streak-header-banner" style={{ position: 'relative' }}>
          <motion.h2
            className="streak-page-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            🔥 Streak Ligi
          </motion.h2>
          <motion.button
            onClick={() => setShowInfoModal(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="info-modal-trigger"
            style={{ position: 'absolute', top: 0, right: 0, background: 'var(--bg-glass)', border: '1px solid var(--border-subtle)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-secondary)' }}
            whileHover={{ scale: 1.1, color: 'var(--accent-primary)', borderColor: 'var(--accent-primary)' }}
          >
            ℹ️
          </motion.button>
          
          <motion.div
            className="streak-motivational-message"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {motivationalMessage}
          </motion.div>
        </div>

        {/* ── Tabs ── */}
        <motion.div
          className="streak-tabs"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => setActiveTab("info")}
            className={`streak-nav-pill ${activeTab === "info" ? "streak-nav-pill--active" : ""}`}
          >
            📅 Kendi Bilgilerim
          </button>
          <button
            onClick={() => setActiveTab("weekly")}
            className={`streak-nav-pill ${activeTab === "weekly" ? "streak-nav-pill--active" : ""}`}
          >
            🏆 Haftalık Lig
          </button>
          <button
            onClick={() => setActiveTab("monthly")}
            className={`streak-nav-pill ${activeTab === "monthly" ? "streak-nav-pill--active" : ""}`}
          >
            🌟 Aylık Lig
          </button>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === "info" && (
            <motion.div
              key="info"
              className="bento-streak-dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* İlerleme ve Grafikler (Sol/Geniş Kısım) */}
              <div className="glass-card bento-streak-col-8">
                <h3 className="streak-card-header">📈 İlerleme Grafiği</h3>
                <div className="my-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <XAxis dataKey="name" stroke="#b0bec5" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="#b0bec5" fontSize={12} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: '8px' }} />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#6366F1"
                        strokeWidth={3}
                        dot={{ fill: "#6366F1", strokeWidth: 2, r: 6 }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Özet ve İstatistikler (Sağ Kısım) */}
              <div className="glass-card bento-streak-col-4">
                <h3 className="streak-card-header">📊 İstatistiklerin</h3>
                <div className="streak-stats-grid mb-6">
                  <div className="streak-stat-box">
                    <span className="streak-stat-label">Mevcut Streak</span>
                    <span className="streak-stat-value highlight">{streaks?.currentStreak || 0}</span>
                  </div>
                  <div className="streak-stat-box">
                    <span className="streak-stat-label">En Uzun Streak</span>
                    <span className="streak-stat-value">{streaks?.longestStreak || 0}</span>
                  </div>
                </div>

                <div className="grid gap-3">
                  {chartData.map((d, i) => (
                    <div key={i} className="flex justify-between items-center p-3 rounded-lg bg-black/30 border border-white/5 text-sm">
                      <strong>{d.name}:</strong> 
                      <span className="text-accent-primary font-bold">{d.count} kez</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {(activeTab === "weekly" || activeTab === "monthly") && (
            <motion.div
              key="leaderboard"
              className="bento-streak-dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="glass-card bento-streak-col-12" style={{position: 'relative'}}>
                <div className="exam-dropdown-container" style={{position: 'absolute', top: '20px', right: '20px', zIndex: 10}}>
                  <select
                    value={selectedExam}
                    onChange={(e) => setSelectedExam(e.target.value)}
                    className="exam-dropdown"
                  >
                    {examTypes.map((exam, index) => (
                      <option key={index} value={exam}>{exam}</option>
                    ))}
                  </select>
                </div>

                <h3 className="streak-card-header">
                  {activeTab === "weekly" ? "🏆 Haftalık Liderler" : "🌟 Aylık Liderler"}
                </h3>

                {leaderboard.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-secondary mb-4">{error || "Lig verisi bulunamadı. Hemen çalışmaya başla ve listeye gir!"}</p>
                  </div>
                ) : (
                <>
                  {/* Podyum */}
                  {top3.length > 0 && (
                    <div className="podium-container">
                      {podiumOrder.map((u, i) => {
                        if (!u) return <div key={i} className="podium-item" style={{opacity:0}}></div>;
                        return (
                          <motion.div 
                            key={u.userId} 
                            className={`podium-item rank-${u.rank}`}
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 + (u.rank * 0.1) }}
                          >
                            <div className="podium-avatar">
                               <Avatar avatarData={u.avatar} seedFallback={u.username} size={u.rank === 1 ? 80 : 60} />
                            </div>
                            <div className="podium-username" title={u.username}>{u.username}</div>
                            <div className="podium-score">{u.streakPoints} Puan</div>
                            <div className="podium-step">{u.rank}</div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Sıralama Listesi */}
                  <div className="leaderboard-list">
                    {rest.map((user) => (
                      <div key={user.userId} className={`leaderboard-row ${user.userId === userId ? "current-user" : ""}`}>
                        <div className="leaderboard-user-info">
                          <div className="rank-badge">{user.rank}</div>
                          <Avatar avatarData={user.avatar} seedFallback={user.username} size={40} />
                          <div>
                            <div className="leaderboard-username">{user.userId === userId ? `${user.username} (Sen)` : user.username}</div>
                            <div className="leaderboard-streak">{user.currentStreak} gün streak</div>
                          </div>
                        </div>
                        <div className="leaderboard-score-area">
                          <span className="score-value">{user.streakPoints} Puan</span>
                          {user.userId !== userId && (
                            <Button variant="ghost" size="sm" onClick={() => handleCongratulate(user.userId)}>
                              🎉
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Kullanıcının Kendi Sırası (Eğer 10 dışındaysa) */}
                  {userRank && userRank.rank > 10 && (
                    <div className="leaderboard-list mt-4">
                       <div className="leaderboard-row current-user">
                        <div className="leaderboard-user-info">
                          <div className="rank-badge">{userRank.rank}</div>
                          <Avatar avatarData={userRank.avatar} seedFallback={userRank.username} size={40} />
                          <div>
                            <div className="leaderboard-username">Sen ({userRank.username})</div>
                            <div className="leaderboard-streak">{userRank.currentStreak || 0} gün streak</div>
                          </div>
                        </div>
                        <div className="leaderboard-score-area">
                          <span className="score-value">{userRank.streakPoints || 0} Puan</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {showInfoModal && (
          <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
            <motion.div 
              className="modal-content" 
              onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{ maxWidth: '400px', background: 'var(--bg-card)' }}
            >
              <h3 className="modal-title" style={{ marginBottom: '16px', fontSize: '1.25rem' }}>ℹ️ Sistem Nasıl Çalışıyor?</h3>
              <ul className="badge-list text-secondary text-sm" style={{ paddingLeft: '1rem', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <li>Her gün giriş yapıp <b>hedef</b> veya <b>plan</b> girdiğinde streak kazanırsın.</li>
                <li>Liderlik tablosundaki puanın (Puan), platformda aktif olduğun eylemlere (çalışma süreleri, tamamlanan hedefler vs.) göre haftalık ve aylık olarak toplanır.</li>
                <li>Haftalık ve aylık ligde ilk 3'e girerek podyumda yerini al!</li>
              </ul>
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="primary" onClick={() => setShowInfoModal(false)}>Anladım</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default StreakPage;